<template>
  <div class="invoice-print-wrapper" id="invoice-page" v-if="loaded">
    <div
      ref="printArea"
      class="invoice-container fade-in"
      :style="{
        '--primary': branding.primary,
        '--secondary': branding.secondary
      }"
    >


      <!-- Header Branding -->
      <div class="branding">
        <img
          v-if="branding.logo"
          :src="branding.logo"
          class="brand-logo"
        />
        <h1 class="brand-title">{{ branding.title }}</h1>
        <p class="brand-meta">{{ branding.address }}</p>
        <p class="brand-meta">
          {{ branding.email }}
          <span v-if="branding.phone"> | {{ branding.phone }}</span>
        </p>
      </div>

      <div class="divider"></div>

      <!-- Invoice Info Block -->
      <div class="invoice-info">
        <div class="left">
          <h2 class="page-title">{{ pageTitle }}</h2>
          <p><b>Number:</b> {{ record.number }}</p>
          <p><b>Date:</b> {{ record.date }}</p>
          <p v-if="record.due_date"><b>Due Date:</b> {{ record.due_date }}</p>
        </div>

        <div class="right">
          <p><b>Client:</b> {{ record.client_name }}</p>
          <p><b>Email:</b> {{ record.client_email }}</p>
          <p><b>Phone:</b> {{ record.client_mobile }}</p>
          <p><b>Address:</b> {{ record.client_address }}</p>
        </div>
      </div>

      <div class="divider"></div>

      <!-- Items Table -->
      <table class="items-table">
        <thead>
          <tr>
            <th>Item</th>
            <th width="80">Qty</th>
            <th width="100">Rate</th>
            <th width="120">Amount</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(item, i) in record.items" :key="i">
            <td>
              <div>{{ item.name }}</div>

              <!-- Breakdown (Matches Add Screens EXACTLY) -->
              <div class="item breakdown" v-if="mainType === 'invoice' || mainType === 'estimate'">

                <small>Base: {{ currency(item.base_amount) }}</small>
                <small>Discount: -{{ currency(item.discount_amount) }}</small>
                <small>Tax: +{{ currency(item.tax_amount) }}</small>
                <small><b>Final: {{ currency(item.final_total) }}</b></small>
              </div>
            </td>

            <td>{{ item.qty }}</td>
            <td>{{ currency(item.rate) }}</td>
            <td>{{ currency(item.final_total) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Final Totals Breakdown -->
      <div class="details-breakdown">
        <h4>Calculation Breakdown</h4>
        <table class="calc-table">
          <tr>
            <td>Subtotal:</td>
            <td>{{ currency(record.subtotal) }}</td>
          </tr>

          <tr>
            <td>Total Discount:</td>
            <td>- {{ currency(record.discount_total) }}</td>
          </tr>
<tr v-if="record.other_amount && record.other_amount != 0">
  <td>Additional Charges:</td>
  <td>{{ currency(record.other_amount) }}</td>
</tr>

          <tr>
            <td>Total Tax:</td>
            <td>+ {{ currency(record.tax_total) }}</td>
          </tr>

          <tr class="grand-row">
            <td><b>Grand Total:</b></td>
            <td><b>{{ currency(record.total) }}</b></td>
          </tr>
        </table>
      </div>

      <!-- Notes -->
      <div v-if="record.notes" class="notes">
        <h3>Notes:</h3>
        <p>{{ record.notes }}</p>
      </div>

      <!-- Print Button -->
      <div style="display: flex; width: 100%; min-width: 100%; text-align: center;">
        <div class="print-btn">
          <button @click="printPage">Print</button>
        </div>
        <div class="print-btn" style="margin-left: 1rem;">
          <button @click="payNow">Pay Now</button>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="loading">Loading...</div>
</template>
<script setup>
import html2pdf from "html2pdf.js";
import { ref, onMounted,nextTick } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { getInvoiceById } from "@/apis/invoiceapis";
import axios from "axios";
import { apiBaseUrl, mainBaseUrl } from "@/apis/appconst";
const toast = useToast();
const route = useRoute();

const loaded = ref(false);

// =============================================
// MAIN RECORD DATA (Unified Structure)
// =============================================
const record = ref({
  number: "",
  date: "",
  due_date: "",
  paypal: "",
  razorpay: "",
  stripe: "",
  client_name: "",
  client_email: "",
  client_mobile: "",
  client_address: "",
  subtotal: 0,
  discount_total: 0,
  tax_total: 0,
  total: 0,
  notes: "",
  items: [],
  creator: {}
});

// =============================================
// BRANDING
// =============================================
const branding = ref({
  logo: "",
  title: "",
  email: "",
  phone: "",
  address: "",
  primary: "#111827",
  secondary: "#6b7280"
});

const pageTitle = ref("Invoice");

// =============================================
// CURRENCY FORMATTER
// =============================================
function currency(n) {
  return "$" + parseFloat(n || 0).toFixed(2);
}

// =============================================
// LOAD DOCUMENT (Invoice/Estimate/PO/Credit Memo)
// =============================================
async function loadDocument() {
  try {
    const mainType = route.params.mainType;
    const id = route.params.invoiceId;

    const res = await getInvoiceById(mainType, id);

    if (!res?.status) {
      alert("Record not found");
      return;
    }

    const doc = res.record;
    const user = doc.user_details || {};

    // --------------------------------------------
    // TITLE BASED ON TYPE
    // --------------------------------------------
    const titles = {
      invoice: "Invoice",
      estimate: "Estimate",
      purchaseorder: "Purchase Order",
      creditmemo: "Credit Memo"
    };
    pageTitle.value = titles[mainType] || "Document";

    // --------------------------------------------
    // MAP MAIN VALUES (UNIFIED)
    // --------------------------------------------
    record.value = {
      // NUMBER
      paypal: res.paypal,
      razorpay: res.razorpay,
      stripe: res.stripe,
      number:
        doc.invoice_number ||
        doc.estimate_number ||
        doc.po_number ||
        doc.credit_number ||
        "",

      // DATE
      date:
        doc.invoice_date ||
        doc.estimate_date ||
        doc.po_order_date ||
        doc.credit_date ||
        "",

      // DUE DATE
      due_date:
        doc.invoice_due_date ||
        doc.estimate_expiry_date ||
        doc.po_delivery_date ||
        "",

      // ===============================
      // FINAL TOTALS (Backend Corrected)
      // ===============================
      subtotal:
  parseFloat(doc.invoice_subtotal) ||
  parseFloat(doc.estimate_subtotal) ||
  parseFloat(doc.po_subtotal) ||
  parseFloat(doc.credit_subtotal) ||
  0,

discount_total:
  parseFloat(doc.invoice_global_discount_value) ||
  parseFloat(doc.estimate_global_discount_value) ||
  0,

tax_total:
  parseFloat(doc.invoice_global_tax_value) ||
  parseFloat(doc.estimate_global_tax_value) ||
  parseFloat(doc.po_tax) ||
  parseFloat(doc.credit_tax) ||
  0,

total:
  parseFloat(doc.invoice_final_total) ||
  parseFloat(doc.estimate_total_amount) ||
  parseFloat(doc.po_total) ||
  parseFloat(doc.credit_total) ||
  0,
  other_amount:
  parseFloat(doc.invoice_other_amount) ||
  parseFloat(doc.estimate_other_amount) ||
  parseFloat(doc.po_other_amount) ||
  0,


      // NOTES
      notes:
        doc.invoice_notes ||
        doc.estimate_notes ||
        doc.po_notes ||
        doc.credit_notes ||
        "",

      // ------------------------------
      // CLIENT DETAILS
      // ------------------------------
      client_name: doc.clientDt.fname+" "+doc.clientDt.lname || "",
      client_email: doc.clientDt?.email || "",
      client_mobile: doc.clientDt?.mobile || "",
      client_address: doc.clientDt?.address_line1 || "",

      // ------------------------------
      // CREATOR DETAILS
      // ------------------------------
      creator: {
        user_id: user.user_id || "",
        name: user.user_name || "",
        email: user.user_email || "",
        mobile: user.user_mobile || "",
        address: user.user_address || "",
        business: user.user_business_name || "",
        logo: apiBaseUrl+user.user_logo || mainBaseUrl+"images/logo.png"
      },

      // ------------------------------
      // ITEM CALCULATIONS (UNIFIED)
      // ------------------------------
      items: (doc.items || []).map((it) => ({
        name:
          it.invoice_item_name ||
          it.estimate_item_name ||
          it.po_item_name ||
          it.credit_item_name ||
          it.name ||
          "",

        qty:
          parseFloat(it.invoice_item_qty) ||
          parseFloat(it.estimate_item_qty) ||
          parseFloat(it.po_item_qty) ||
          parseFloat(it.credit_item_qty) ||
          1,

        rate:
          parseFloat(it.invoice_item_rate) ||
          parseFloat(it.estimate_item_rate) ||
          parseFloat(it.po_item_rate) ||
          parseFloat(it.credit_item_rate) ||
          0,

        // BACKEND-CORRECTED VALUES
        base_amount: 
  parseFloat(it.invoice_item_amount) ||
  parseFloat(it.estimate_item_amount) ||
  parseFloat(it.po_item_amount) ||
  parseFloat(it.credit_item_amount) ||
  0,

discount_amount:
  parseFloat(it.invoice_item_discount) ||
  parseFloat(it.item_discount_amount) ||
  0,

tax_amount:
  parseFloat(it.invoice_item_tax) ||
  parseFloat(it.item_tax_amount) ||
  0,

final_total:
  parseFloat(it.invoice_item_total) ||
  parseFloat(it.item_total) ||
  parseFloat(it.po_item_amount) || 
  0

      }))
    };

    

    // =============================================
    // BRANDING (Unified)
    // =============================================
    branding.value = {
      logo:
      doc.invoice_logo || 
      doc.estimate_logo ||
      doc.po_logo ||
      doc.credit_logo ||
      apiBaseUrl+user.user_logo || mainBaseUrl+"images/logo.png",


      title:
        doc.branding_title ||
        user.user_business_name ||
        "Business",

      email:
        doc.branding_email ||
        user.user_email ||
        "",

      phone:
        doc.branding_phone ||
        user.user_mobile ||
        "",

      address:
        doc.branding_address ||
        user.user_address ||
        "",

      primary:
        doc.invoice_theme_color ||
        doc.estimate_theme_color ||
        doc.po_theme_color ||
        doc.credit_theme_color ||
        user.user_primary_color ||
        "#111827",


      secondary:
        doc.invoice_secondary_color ||
        user.user_secondary_color ||
        "#6b7280"
    };

    loaded.value = true;
   await nextTick();

    setTimeout(() => {
      downloadPDF();
    }, 2000);
  } catch (err) {
    console.error(err);
  }
}

// =============================================
// PRINT FUNCTION
// =============================================
function printPage() {
  window.print();
}
/* =============================================================
   POPUP WINDOW CENTERING
============================================================= */
function openCenteredPopup(url = "", width = 600, height = 750) {
  const dualLeft = window.screenLeft ?? window.screenX;
  const dualTop = window.screenTop ?? window.screenY;

  const w = window.innerWidth || document.documentElement.clientWidth;
  const h = window.innerHeight || document.documentElement.clientHeight;

  const left = (w / 2 - width / 2) + dualLeft;
  const top = (h / 2 - height / 2) + dualTop;

  return window.open(
    url,
    "_blank",
    `scrollbars=yes,width=${width},height=${height},top=${top},left=${left}`
  );
}

/* =============================================================
   GATEWAY SELECTION POPUP (VUE-COMPATIBLE)
============================================================= */
function showGatewaySelector(gateways, callback) {
  const modal = document.createElement("div");
  modal.id = "gatewayModal";
  Object.assign(modal.style, {
    position: "fixed",
    inset: "0",
    background: "rgba(0,0,0,0.55)",
    zIndex: "99999",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  });

  const box = document.createElement("div");
  Object.assign(box.style, {
    background: "#fff",
    width: "340px",
    padding: "25px",
    borderRadius: "14px",
    textAlign: "center",
    fontFamily: "Inter, sans-serif"
  });

  box.innerHTML = `
    <h3 style="font-size:20px;font-weight:700;margin-bottom:15px;">
      Choose Payment Gateway
    </h3>
    <div id="gwBtnContainer"></div>
  `;

  gateways.forEach(g => {
    const btn = document.createElement("button");
    btn.innerText = g.toUpperCase();
    Object.assign(btn.style, {
      width: "100%",
      marginTop: "10px",
      padding: "12px",
      background: branding.value.primary,
      color: "#fff",
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      fontWeight: "600"
    });

    btn.onclick = () => {
      modal.remove();
      callback(g);
    };

    box.querySelector("#gwBtnContainer").appendChild(btn);
  });

  modal.appendChild(box);
  document.body.appendChild(modal);
}

/* =============================================================
   PROCESS PAYMENT
============================================================= */
function processGateway(gateway, user_id, amount) {
  let popup = openCenteredPopup("", 600, 750);

  const endpoints = {
    paypal: apiBaseUrl + "/paypal1/makePayment",
    razorpay: apiBaseUrl + "/razorpay1/makePayment",
    stripe: apiBaseUrl + "/stripe1/makePayment"
  };

  axios.post(
    endpoints[gateway],
    new URLSearchParams({ user_id, amount }),
    { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
  )
  .then(res => {
    if (!res.data.status) {
      toast.error(res.data.success || "Payment failed.");
      popup.close();
      return;
    }

    popup.location.href = res.data.url;
  })
  .catch(err => {
    console.error(err);
    toast.error("Gateway request failed.");
    popup.close();
  });
}

/* =============================================================
   MAIN PAY NOW HANDLER
============================================================= */
function payNow() {
  const invoiceAmount = record.value.total;
  const creatorUserId = record.value.creator?.user_id;

  if (!invoiceAmount || !creatorUserId) {
    toast.error("Invoice or creator data missing.");
    return;
  }

  const active = [];
  if (record.value.paypal) active.push("paypal");
  if (record.value.razorpay) active.push("razorpay");
  if (record.value.stripe) active.push("stripe");

  if (active.length === 0) {
    toast.error("No payment gateway enabled.");
    return;
  }

  if (active.length === 1) {
    toast.success("Redirecting to payment...");
    processGateway(active[0], creatorUserId, invoiceAmount);
    return;
  }

  // multi-gateway → popup
  showGatewaySelector(active, g => {
    toast.success("Starting " + g.toUpperCase() + " payment...");
    processGateway(g, creatorUserId, invoiceAmount);
  });
}

/* =============================================================
   PAYMENT CALLBACK FROM POPUP
============================================================= */
window.addEventListener("message", function (event) {
  if (!event.data) return;

  if (event.data.status === "success") {
    toast.success("Payment Successful!");
  }

  if (event.data.status === "error") {
    toast.error("Payment Failed: " + event.data.message);
  }
});

const downloadPDF = ()=>{

  const el = document.getElementById("invoice-page");
  const timestamp = Math.floor(Date.now() / 1000);
  const options = {
    margin: 10,
    filename: "invoice"+timestamp+".pdf",
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true
    },
    jsPDF: {
      unit: "mm",
      format: "a4",
      orientation: "portrait"
    }
  };

  html2pdf()
    .from(el)
    .set(options)
    .save();
  
}


onMounted(loadDocument);


</script>

<style scoped>
/* --------------------------------------------------
   MAIN CONTAINER
-------------------------------------------------- */
.invoice-container {
  padding: 40px;
  background: #fff;
  max-width: 900px;
  margin: auto;
  font-family: "Inter", sans-serif;
  position: relative;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.06);
  border: 1px solid #eef0f3;

  --primary: #111827;
  --secondary: #6b7280;
}

/* Smooth Fade */
.fade-in {
  animation: fadeIn 0.6s ease-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}


/* --------------------------------------------------
   BRANDING / HEADER
-------------------------------------------------- */
.branding {
  text-align: center;
  margin-bottom: 30px;
}

.brand-logo {
  width: 100px;
  height: auto;
  object-fit: contain;
  margin-bottom: 8px;
}

.brand-title {
  font-size: 1.9rem;
  font-weight: 700;
  color: var(--primary);
  letter-spacing: -0.5px;
}

.brand-meta {
  color: #6b7280;
  font-size: 0.9rem;
  margin-top: 3px;
}

/* Divider */
.divider {
  height: 1px;
  background: #e5e7eb;
  margin: 30px 0;
}

/* --------------------------------------------------
   INFO BLOCKS (LEFT / RIGHT)
-------------------------------------------------- */
.invoice-info {
  display: flex;
  justify-content: space-between;
  gap: 40px;
}

.invoice-info .left p,
.invoice-info .right p {
  margin: 4px 0;
  font-size: 0.95rem;
  color: #374151;
}

.page-title {
  font-size: 1.7rem;
  font-weight: 700;
  margin-bottom: 10px;
  color: var(--primary);
}

/* --------------------------------------------------
   ITEMS TABLE (PREMIUM THEME)
-------------------------------------------------- */
.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 0.95rem;
}

.items-table thead {
  background: var(--primary);
  color: #fff;
}

.items-table th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
}

.items-table th:last-child {
  border-right: none;
}

.items-table td {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  color: #374151;
}

/* Zebra rows */
.items-table tbody tr:nth-child(odd) td {
  background: #fafafa;
}

/* Hover */
.items-table tbody tr:hover td {
  background: #f3f4f6;
  transition: background 0.2s ease;
}

/* --------------------------------------------------
   TOTAL SECTION
-------------------------------------------------- */
.total-section {
  margin-top: 24px;
  text-align: right;
  font-size: 1rem;
}

.total-section p {
  margin: 6px 0;
  font-weight: 500;
}

.grand-total {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--primary);
  margin-top: 10px;
}

/* --------------------------------------------------
   NOTES BOX
-------------------------------------------------- */
.notes {
  margin-top: 35px;
  padding: 18px;
  background: #f9fafb;
  border-radius: 10px;
}

.notes h3 {
  margin: 0 0 5px 0;
  color: var(--primary);
}

.notes p {
  font-size: 0.95rem;
  color: #374151;
  line-height: 1.5;
}

/* --------------------------------------------------
   PRINT BUTTON
-------------------------------------------------- */
.print-btn {
  text-align: center;
  margin-top: 40px;
}

.print-btn button {
  background: var(--primary);
  color: #fff;
  padding: 12px 26px;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  letter-spacing: 0.3px;
  transition: 0.25s ease;
  box-shadow: 0 6px 14px rgba(0,0,0,0.12);
}

.print-btn button:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.18);
}



.brand-title {
  color: v-bind('branding.primary');
}

.items-table thead {
  background: v-bind('branding.primary');
}

.grand-total {
  color: v-bind('branding.primary');
}

.print-btn button {
  background: v-bind('branding.primary');
}
/* --------------------------------------------------
   ITEM CALCULATION BREAKDOWN (inside item cell)
-------------------------------------------------- */
.item.breakdown {
  margin-top: 4px;
  color: #6b7280;
  font-size: 0.78rem;
  line-height: 1.25rem;
}

.item.breakdown small {
  display: block;
}

/* --------------------------------------------------
   TOTAL CALCULATION BREAKDOWN
-------------------------------------------------- */
.details-breakdown {
  margin-top: 25px;
  padding: 18px;
  background: #fafafa;
  border-radius: 10px;
}

.details-breakdown h4 {
  margin: 0 0 12px 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--primary);
}

.calc-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 6px;
}

.calc-table td {
  padding: 6px 0;
  font-size: 0.95rem;
  color: #374151;
}

.calc-table td:last-child {
  text-align: right;
  font-weight: 600;
}

.calc-table .grand-row td {
  padding-top: 12px;
  font-size: 1.15rem;
  font-weight: 800 !important;
  color: var(--primary);
}
/* --------------------------------------------------
   MOBILE RESPONSIVE PATCH
   For screens below 640px
-------------------------------------------------- */
@media (max-width: 640px) {

  .invoice-container {
    padding: 20px !important;
  }

  /* Reduce header logo size */
  .brand-logo {
    width: 70px;
  }

  /* Smaller title */
  .brand-title {
    font-size: 1.4rem;
  }

  /* Center header text */
  .branding {
    margin-bottom: 18px;
  }

  /* Stack invoice-info columns */
  .invoice-info {
    flex-direction: column !important;
    gap: 12px !important;
  }

  .invoice-info .left,
  .invoice-info .right {
    width: 100%;
  }

  .page-title {
    font-size: 1.3rem !important;
  }

  /* ITEMS TABLE RESPONSIVE */
  .items-table th,
  .items-table td {
    padding: 8px 6px !important;
    font-size: 0.85rem !important;
  }

  .items-table thead {
    font-size: 0.85rem;
  }

  /* Allow item description block to wrap cleanly */
  .item.breakdown {
    font-size: 0.75rem;
    line-height: 1.2rem;
  }

  /* Total section spacing */
  .total-section {
    font-size: 0.9rem;
  }

  .grand-total {
    font-size: 1.2rem !important;
  }

  /* Breakdown box */
  .details-breakdown {
    padding: 14px !important;
  }

  .details-breakdown h4 {
    font-size: 1rem;
  }

  .calc-table td {
    font-size: 0.85rem !important;
  }

  /* Print button */
  .print-btn button {
    width: 100%;
    padding: 12px;
    font-size: 1rem !important;
  }
}

</style>

